import { Injectable } from '@angular/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';

import { UsageMeasurementService } from './usage-measurement.service';

/**
 * Where an activity or group was opened from.
 *
 * This is the parameter that pays off later: it is what will let participation
 * be attributed back to a channel once acquisition tracking exists. It cannot be
 * derived after the fact, so it has to be passed in at the call site.
 */
export type ViewSource = 'list' | 'search' | 'notification' | 'group_link' | 'deep_link' | 'payment';

/** Where a sport was added to a profile. */
export type SportSource = 'onboarding' | 'profile_edit' | 'nudge';

type EventParams = Record<string, string | number | boolean | undefined | null>;

/**
 * Custom Firebase Analytics events.
 *
 * Automatic events (first_open, session_start, screen_view) already flow: the
 * SDK is installed and the GA4 property was linked on 12 Aug 2026. They say how
 * many people open the app, not what they do in it. These are the ones that
 * answer product questions.
 *
 * Three rules this class exists to enforce in one place, rather than hoping
 * eight call sites remember them:
 *
 * 1. Analytics must never break a user flow. Every call is wrapped: a failure
 *    is logged to the console and swallowed. Nobody should fail to join an
 *    activity because a measurement call threw.
 *
 * 2. No personal data in parameters. No email, no name, no free text written by
 *    a user. Identity already flows separately through setUserId. Parameters
 *    here are ids, enums and numbers.
 *
 * 3. snake_case names and parameters, GA4 convention, and it keeps the BigQuery
 *    export readable.
 *
 * 4. A MEMBER WHO REFUSED THE MEASUREMENT IS NOT MEASURED. Checked here, once,
 *    rather than at each call site, and checked before the parameters are even
 *    built. Firebase's own `setEnabled` only applies at the next run of the app
 *    (its documentation, not a measurement of ours), so without this guard a
 *    refusal would leave every event below still being sent for the rest of the
 *    session. This is the half of the refusal that is entirely in our hands.
 *
 * Undefined and null parameters are dropped rather than sent: GA4 stores them
 * as the literal string "undefined" otherwise, which quietly pollutes reports.
 */
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  constructor(private readonly usageMeasurement: UsageMeasurementService) {}

  /**
   * A user opened an activity detail page.
   *
   * The most valuable of the set. The database knows who joined, never who
   * looked. Without this there is no way to tell "I saw nothing that interested
   * me" from "I saw it and did not take the step", and those two failures need
   * opposite fixes.
   */
  logActivityView(params: {
    activityId: string;
    sport?: string;
    city?: string;
    isPaid: boolean;
    price?: number;
    source?: ViewSource;
  }): void {
    this.log('activity_view', {
      activity_id: params.activityId,
      sport: params.sport,
      city: params.city,
      is_paid: params.isPaid,
      price: params.price,
      source: params.source,
    });
  }

  /** A join succeeded. Paired with activity_view, this gives the conversion rate. */
  logActivityJoin(params: {
    activityId: string;
    sport?: string;
    city?: string;
    isPaid: boolean;
    seatsLeft?: number;
  }): void {
    this.log('activity_join', {
      activity_id: params.activityId,
      sport: params.sport,
      city: params.city,
      is_paid: params.isPaid,
      seats_left: params.seatsLeft,
    });
  }

  /**
   * A sport was added to a profile.
   *
   * 44% of profiles declare no sport and are structurally unmatchable. This is
   * how the effect of completion campaigns gets measured.
   */
  logSportDeclared(params: { sport?: string; isFirstSport: boolean; source: SportSource }): void {
    this.log('sport_declared', {
      sport: params.sport,
      is_first_sport: params.isFirstSport,
      source: params.source,
    });
  }

  /**
   * A search ran.
   *
   * A search returning zero results is demand with no supply behind it, which is
   * what turns the city ranking from descriptive into predictive.
   */
  logSearchPerformed(params: {
    querySport?: string;
    queryCity?: string;
    queryDate?: string;
    resultsCount: number;
  }): void {
    this.log('search_performed', {
      query_sport: params.querySport,
      query_city: params.queryCity,
      query_date: params.queryDate,
      results_count: params.resultsCount,
    });
  }

  /** A user opened a group page. Same logic as activities: seen, or not seen. */
  logGroupView(params: {
    groupId: string;
    sport?: string;
    city?: string;
    memberCount?: number;
    source?: ViewSource;
  }): void {
    this.log('group_view', {
      group_id: params.groupId,
      sport: params.sport,
      city: params.city,
      member_count: params.memberCount,
      source: params.source,
    });
  }

  /** A user joined a group. 75% of groups have at most one member. */
  /**
   * Sending a join request, which leaves the user PENDING. This is not
   * membership and must not be counted as one: the request may be refused, or
   * never answered. Found by QC, which saw group_join fired on the request.
   */
  logGroupJoinRequest(params: { groupId: string; sport?: string; city?: string; memberCount?: number }): void {
    this.log('group_join_request', {
      group_id: params.groupId,
      sport: params.sport,
      city: params.city,
      member_count: params.memberCount,
    });
  }

  /**
   * Actual membership. The only moment the app observes this is the user
   * accepting an invitation; an admin approving a pending request happens
   * elsewhere and is invisible here. Parameters are therefore thinner than on
   * the request event, which is the right trade: a correct event with fewer
   * fields beats a rich one that is wrong.
   */
  logGroupJoin(params: { groupId: string; sport?: string; city?: string; memberCount?: number }): void {
    this.log('group_join', {
      group_id: params.groupId,
      sport: params.sport,
      city: params.city,
      member_count: params.memberCount,
    });
  }

  /** Payment was initiated. Its distance from payment_completed is the abandoned basket. */
  logPaymentStarted(params: { activityId: string; amount: number; currency: string }): void {
    this.log('payment_started', {
      activity_id: params.activityId,
      amount: params.amount,
      currency: params.currency,
    });
  }

  /**
   * Payment finished, successfully or not.
   *
   * `success` separates a technical blocker from a change of mind, which is the
   * whole point: three transactions are currently stuck pending against very
   * little actually collected, and today there is no way to tell which is which.
   */
  logPaymentCompleted(params: {
    /*
     * Optional: the payment state holds the activity as `EventDetailOutputDto |
     * null`, so a payment reported after the state was cleared has no id to
     * name. GA4 simply receives the event without that parameter, which is
     * better than not reporting the payment at all.
     */
    activityId?: string;
    amount: number;
    currency: string;
    success: boolean;
    failureReason?: string;
  }): void {
    this.log('payment_completed', {
      activity_id: params.activityId,
      amount: params.amount,
      currency: params.currency,
      success: params.success,
      failure_reason: params.failureReason,
    });
  }

  private log(name: string, params: EventParams): void {
    if (!this.usageMeasurement.isAllowed()) {
      return;
    }

    const clean = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== null),
    );

    // Deliberately fire-and-forget with a catch rather than awaited: measurement
    // must not sit in the way of what the user asked for, and must not be able
    // to fail it either.
    FirebaseAnalytics.logEvent({ name, params: clean }).catch((error: unknown) => {
      console.warn(`[Analytics] ${name} was not recorded`, error);
    });
  }
}
